import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  ArrowRight, ArrowLeftRight, BarChart3, ChevronDown, ChevronRight, Code2, FileText, Link2, ListChecks,
  Mail, MousePointerClick, Palette, PenLine, Plug, Search, Share2, ShoppingCart, Smartphone, Sparkles,
  Store, Terminal, TrendingUp, Users, Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Platform, PlatformHizmet, Vitrin } from "@/lib/platform_hizmet_data";

/**
 * Menü etiketleri statik; 90KB'lık hizmet içerik verisi (platform_hizmet_data.ts)
 * yalnızca menü açıldığında / üzerine gelindiğinde indirilir (ana paket küçülür).
 */
const MENU: { key: Platform["key"]; etiket: string; ad: string }[] = [
  { key: "shopify", etiket: "Shopify Hizmetleri", ad: "Shopify" },
  { key: "ikas", etiket: "ikas Hizmetleri", ad: "ikas" },
];
let veriSozu: Promise<Platform[]> | null = null;
export const platformVerisiniYukle = () =>
  (veriSozu ??= import("@/lib/platform_hizmet_data").then(m => m.PLATFORMLAR));
function usePlatformVerisi(tetik: boolean) {
  const [veri, setVeri] = useState<Platform[] | null>(null);
  useEffect(() => {
    if (!tetik || veri) return;
    let iptal = false;
    platformVerisiniYukle().then(v => { if (!iptal) setVeri(v); });
    return () => { iptal = true; };
  }, [tetik, veri]);
  return veri;
}

export const IKONLAR: Record<string, LucideIcon> = {
  Store, Palette, Plug, Wrench, Smartphone, ArrowLeftRight, ListChecks, Code2, Share2, BarChart3,
  ShoppingCart, Mail, Users, Search, Terminal, Link2, PenLine, FileText, TrendingUp, Sparkles,
};

const hizmet = (p: Platform, slug: string) => p.hizmetler.find(h => h.slug === slug) as PlatformHizmet;

function HizmetSatiri({ p, h, onClick }: { p: Platform; h: PlatformHizmet; onClick: () => void }) {
  const Icon = IKONLAR[h.icon] ?? Store;
  return (
    <Link href={`/${p.key}/${h.slug}`} onClick={onClick} className="group flex items-start gap-3 py-3 border-b border-neutral-200 last:border-0 hover:bg-neutral-50 -mx-2 px-2 transition-colors">
      <span className="mt-0.5 w-8 h-8 shrink-0 border-2 border-black bg-white flex items-center justify-center group-hover:bg-[#9EFF00] transition-colors">
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 font-bold text-sm">
          {h.baslik}
          {h.rozet && <span className="text-[9px] font-mono font-bold border border-black px-1 py-px bg-[#9EFF00]">{h.rozet}</span>}
        </span>
        <span className="block text-xs text-neutral-500 font-medium leading-snug">{h.kisa}</span>
      </span>
      <ChevronRight className="w-4 h-4 mt-2 text-neutral-400 group-hover:text-black group-hover:translate-x-0.5 transition-all shrink-0" />
    </Link>
  );
}

/** Öne çıkan ürün kartı (ör. Dijipo x Madrid Teması) */
export function VitrinKarti({ v, onClick }: { v: Vitrin; onClick?: () => void }) {
  return (
    <div className="mt-4 border-3 border-black bg-[#9EFF00] p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="font-mono text-[10px] font-bold tracking-widest">ÖNE ÇIKAN TEMA</span>
          <span className="block font-black text-base leading-tight mt-0.5">{v.baslik}</span>
        </div>
        {v.fiyat && <span className="shrink-0 bg-black text-[#9EFF00] font-black text-sm px-2 py-1">{v.fiyat}</span>}
      </div>
      <p className="text-xs font-semibold text-neutral-800 leading-snug mt-2">{v.aciklama}</p>
      <div className="flex flex-wrap gap-2 mt-3">
        <Link href={v.href} onClick={onClick} className="bg-black text-[#9EFF00] border-2 border-black px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1">
          {v.etiket} <ChevronRight className="w-3.5 h-3.5" />
        </Link>
        {v.demo && (
          <a href={v.demo} target="_blank" rel="noopener" onClick={onClick} className="bg-white border-2 border-black px-3 py-1.5 text-xs font-bold">
            Canlı demo
          </a>
        )}
        {v.kilavuz && (
          <Link href={v.kilavuz} onClick={onClick} className="bg-white border-2 border-black px-3 py-1.5 text-xs font-bold">
            Kullanım kılavuzu
          </Link>
        )}
      </div>
    </div>
  );
}

/** Masaüstü mega menü paneli (tek platform) */
function Panel({ p, onNavigate }: { p: Platform; onNavigate: () => void }) {
  const [aktif, setAktif] = useState(0);
  const n = p.niyetler[aktif];
  const one = hizmet(p, n.oneCikan.slug);
  const OneIcon = IKONLAR[one.icon] ?? Store;
  const ikiSutun = !!n.ikinciHizmetler;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
      <div className="grid grid-cols-12 gap-6">
        {/* Sol: niyet listesi */}
        <div className="col-span-4 xl:col-span-3 bg-neutral-50 border-3 border-black p-4 flex flex-col">
          <div className="font-mono text-[10px] font-bold tracking-widest text-neutral-500 mb-3">NE YAPMAK İSTİYORSUNUZ?</div>
          <ul className="space-y-1">
            {p.niyetler.map((ny, i) => (
              <li key={ny.no}>
                <button
                  onMouseEnter={() => setAktif(i)}
                  onFocus={() => setAktif(i)}
                  onClick={() => setAktif(i)}
                  className={`w-full text-left flex gap-3 px-3 py-3 border-l-4 transition-colors ${i === aktif ? "bg-white border-[#9EFF00] shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] border-y-2 border-r-2 border-y-black border-r-black" : "border-transparent hover:bg-white/70"}`}
                >
                  <span className={`font-mono text-[11px] font-bold mt-0.5 ${i === aktif ? "text-black" : "text-neutral-400"}`}>{ny.no}</span>
                  <span>
                    <span className="block font-bold text-sm leading-tight">{ny.baslik}</span>
                    <span className="block text-xs text-neutral-500 font-medium leading-snug mt-0.5">{ny.alt}</span>
                  </span>
                </button>
                {i === aktif && ny.cipler && (
                  <div className="px-3 pt-3 pb-1">
                    <div className="font-mono text-[10px] font-bold tracking-widest text-neutral-500 mb-2">{ny.cipBaslik}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {ny.cipler.map(c => (
                        <Link key={c.etiket} href={c.href} onClick={onNavigate} className={`text-xs font-bold border-2 px-2.5 py-1 bg-white hover:bg-[#9EFF00] transition-colors ${c.vurgu ? "border-dashed border-red-500 text-red-600" : "border-black"}`}>
                          {c.etiket}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <Link href={`/${p.key}`} onClick={onNavigate} className="mt-auto pt-4 font-bold text-sm inline-flex items-center gap-1 hover:underline">
            Tüm hizmetler <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Sağ: içerik */}
        <div className={`col-span-8 xl:col-span-9 grid gap-8 ${ikiSutun ? "grid-cols-2" : "grid-cols-1"}`}>
          <div>
            <div className="font-mono text-[10px] font-bold tracking-widest text-neutral-500 border-b border-neutral-200 pb-2 mb-3">{n.kategori}</div>
            {!ikiSutun && (
              <Link href={`/${p.key}/${one.slug}`} onClick={onNavigate} className="group flex items-center gap-4 border-3 border-black bg-white p-4 mb-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(158,255,0,1)] transition-all">
                <span className="w-11 h-11 shrink-0 border-2 border-black bg-[#9EFF00] flex items-center justify-center"><OneIcon className="w-5 h-5" /></span>
                <span className="flex-1 min-w-0">
                  <span className="block font-black text-base">{one.baslik}</span>
                  <span className="block text-xs text-neutral-600 font-medium leading-snug">{n.oneCikan.aciklama || one.kisa}</span>
                </span>
                <span className="shrink-0 bg-black text-[#9EFF00] font-mono font-bold text-[11px] px-3 py-2 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                  <MousePointerClick className="w-3.5 h-3.5" /> TIKLA <ChevronRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            )}
            <div>
              {n.hizmetler.map(s => <HizmetSatiri key={s} p={p} h={hizmet(p, s)} onClick={onNavigate} />)}
            </div>
            {n.vitrin && <VitrinKarti v={n.vitrin} onClick={onNavigate} />}
          </div>
          {ikiSutun && (
            <div>
              <div className="font-mono text-[10px] font-bold tracking-widest text-neutral-500 border-b border-neutral-200 pb-2 mb-3">{n.ikinciKategori}</div>
              {n.ikinciHizmetler!.map(s => <HizmetSatiri key={s} p={p} h={hizmet(p, s)} onClick={onNavigate} />)}
            </div>
          )}
        </div>
      </div>

      {/* Alt istatistik şeridi */}
      <div className="mt-6 pt-4 border-t border-neutral-200 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs font-medium text-neutral-500">
        {p.istatistik.map((s, i) => (
          <span key={s} className="flex items-center gap-6">
            {i > 0 && <span className="w-1 h-1 rounded-full bg-neutral-300" />}
            <span><strong className="text-black">{s.split(" ")[0]}</strong> {s.split(" ").slice(1).join(" ")}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Header'a yerleşen tetikleyiciler + tam genişlik panel (masaüstü) */
export function MegaMenuNav() {
  const [acik, setAcik] = useState<Platform["key"] | null>(null);
  const [location] = useLocation();
  const kapatZamanlayici = useRef<number | null>(null);

  useEffect(() => { setAcik(null); }, [location]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setAcik(null);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const veri = usePlatformVerisi(acik !== null);
  const ac = (k: Platform["key"]) => { if (kapatZamanlayici.current) window.clearTimeout(kapatZamanlayici.current); void platformVerisiniYukle(); setAcik(k); };
  const gecikmeliKapat = () => { kapatZamanlayici.current = window.setTimeout(() => setAcik(null), 160); };

  return (
    <>
      {MENU.map(p => (
        <button
          key={p.key}
          onMouseEnter={() => ac(p.key)}
          onMouseLeave={gecikmeliKapat}
          onClick={() => setAcik(acik === p.key ? null : p.key)}
          aria-expanded={acik === p.key}
          className={`inline-flex items-center gap-1 hover:text-neutral-600 transition-colors ${location.startsWith(`/${p.key}`) ? "underline decoration-[#9EFF00] decoration-4" : ""}`}
        >
          {p.etiket}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${acik === p.key ? "rotate-180" : ""}`} />
        </button>
      ))}
      {acik && (
        <div
          onMouseEnter={() => ac(acik)}
          onMouseLeave={gecikmeliKapat}
          className="absolute left-0 right-0 top-full bg-white border-b-3 border-black shadow-[0_12px_0_0_rgba(0,0,0,0.06)] z-50"
        >
          {veri
            ? <Panel key={acik} p={veri.find(p => p.key === acik)!} onNavigate={() => setAcik(null)} />
            : <div className="max-w-7xl mx-auto px-8 py-10 font-mono text-xs font-bold tracking-widest text-neutral-500">YÜKLENİYOR…</div>}
        </div>
      )}
    </>
  );
}

/** Mobil menü için akordeon */
export function MegaMenuMobile({ onNavigate }: { onNavigate: () => void }) {
  const [acik, setAcik] = useState<string | null>(null);
  const veri = usePlatformVerisi(true);
  return (
    <>
      {MENU.map(m => {
        const p = veri?.find(v => v.key === m.key);
        return (
        <div key={m.key} className="border-b border-neutral-100">
          <button onClick={() => setAcik(acik === m.key ? null : m.key)} aria-expanded={acik === m.key} className="w-full flex items-center justify-between py-2 font-bold text-sm">
            {m.etiket}
            <ChevronDown className={`w-4 h-4 transition-transform ${acik === m.key ? "rotate-180" : ""}`} />
          </button>
          {acik === m.key && p && (
            <div className="pb-3 space-y-3">
              {p.niyetler.map(n => (
                <div key={n.no} className="border-l-4 border-[#9EFF00] pl-3">
                  <div className="font-mono text-[10px] font-bold tracking-widest text-neutral-500">{n.no} · {n.baslik.toUpperCase()}</div>
                  <ul className="mt-1 space-y-1">
                    {[n.oneCikan.slug, ...n.hizmetler, ...(n.ikinciHizmetler ?? [])].filter((s, i, a) => a.indexOf(s) === i).map(s => (
                      <li key={s}><Link href={`/${p.key}/${s}`} onClick={onNavigate} className="block text-sm font-semibold py-1 text-neutral-800">{hizmet(p, s).baslik}</Link></li>
                    ))}
                  </ul>
                  {n.vitrin && (
                    <Link href={n.vitrin.href} onClick={onNavigate} className="mt-1 inline-flex items-center gap-2 bg-[#9EFF00] border-2 border-black px-2 py-1 text-sm font-bold">
                      {n.vitrin.baslik}{n.vitrin.fiyat ? ` · ${n.vitrin.fiyat}` : ""}
                    </Link>
                  )}
                </div>
              ))}
              <Link href={`/${p.key}`} onClick={onNavigate} className="inline-flex items-center gap-1 text-sm font-bold underline">Tüm {p.ad} hizmetleri <ArrowRight className="w-4 h-4" /></Link>
            </div>
          )}
        </div>
        );
      })}
    </>
  );
}
